import type * as API from "../data/api-types";

type StaffStatus = "in_progress" | "waiting" | "resolved";

/** The status a Classic status select sets, keyed by the named verb that sets it (spec 10 §3.2, ruling 6). */
const STATUS_BY_COMMAND: Record<string, StaffStatus> = {
  "workflow.mark_ticket_in_progress": "in_progress",
  "workflow.mark_ticket_waiting": "waiting",
  "workflow.resolve_ticket": "resolved",
};

/**
 * How Classic's status select offers each status the named verbs set, read from the ticket record's presented
 * commands so the select and the object UI agree (hide or block, Ben's ruling 15 Oct):
 * - `hidden`: the verb is not presented, because it does not apply (a closed ticket shows only Reopen; the status
 *   the ticket is already in is not offered). The current status stays visible as the select's value.
 * - `disabled`: the verb is presented but blocked, with its reason.
 * A record that carries no commands (an older response) hides and disables nothing.
 */
export function ticketStatusAvailability(
  ticket: Pick<API.TicketRecordView, "status" | "commands">,
): { hidden: StaffStatus[]; disabled: Partial<Record<StaffStatus, string>> } {
  if (!ticket.commands) return { hidden: [], disabled: {} };
  const presented = new Map(ticket.commands.map((c) => [c.name, c]));
  const hidden: StaffStatus[] = [];
  const disabled: Partial<Record<StaffStatus, string>> = {};
  for (const [name, status] of Object.entries(STATUS_BY_COMMAND)) {
    if (status === ticket.status) continue;
    const command = presented.get(name);
    if (!command) hidden.push(status);
    else if (!command.enabled && command.reason)
      disabled[status] = command.reason;
  }
  return { hidden, disabled };
}
